import type { ComponentPropsWithoutRef, ReactNode } from "react";

import { useCommunitySwitcherContext } from "../context/CommunitySwitcherContext";
import { cn } from "../helpers/cn";
import { CommunitySwitcherCircleAiButton } from "./CommunitySwitcherCircleAiButton";
import { CommunitySwitcherCommunityList } from "./CommunitySwitcherCommunityList";
import { CommunitySwitcherCurrentCommunityLogo } from "./CommunitySwitcherCurrentCommunityLogo";
import { CommunitySwitcherDiscoverButton } from "./CommunitySwitcherDiscoverButton";
import { CommunitySwitcherDivider } from "./CommunitySwitcherDivider";
import { CommunitySwitcherLoading } from "./CommunitySwitcherLoading";
import { CommunitySwitcherNewCommunityButton } from "./CommunitySwitcherNewCommunityButton";

export interface CommunitySwitcherPresentationProps extends Omit<
  ComponentPropsWithoutRef<"div">,
  "children"
> {
  readonly children?: ReactNode;
  readonly isLoading?: boolean;
  readonly onClickSeeMyCommunities?: () => void;
  readonly shouldShowShowMoreButton?: boolean;
}

export const CommunitySwitcherPresentation = ({
  children,
  className,
  isLoading = false,
  onClickSeeMyCommunities = () => {},
  shouldShowShowMoreButton,
  ...rest
}: CommunitySwitcherPresentationProps) => {
  const { featureFlags } = useCommunitySwitcherContext();

  if (isLoading) {
    return <CommunitySwitcherLoading />;
  }

  const showSidebar = featureFlags?.showSidebar !== false;
  if (!showSidebar) {
    return null;
  }

  const showCommunities = Boolean(featureFlags?.showCommunities);

  const showNewCommunityButton = Boolean(featureFlags?.showNewCommunityButton);

  const showDiscoverButton = Boolean(featureFlags?.showDiscoverButton);

  return (
    <div
      className={cn("w-18", className)}
      data-testid="community-switcher-presentation"
      {...rest}
    >
      <div
        className="flex min-h-screen flex-col items-center gap-4 overflow-hidden border-r border-primary bg-secondary p-4 dark:border-secondary"
        data-testid="community-switcher-presentation-body"
      >
        {showDiscoverButton ? (
          <CommunitySwitcherDiscoverButton />
        ) : (
          <CommunitySwitcherCurrentCommunityLogo />
        )}
        <CommunitySwitcherCircleAiButton />
        {showCommunities && (
          <>
            <CommunitySwitcherDivider />
            <CommunitySwitcherCommunityList
              onClickSeeMyCommunities={onClickSeeMyCommunities}
              shouldShowShowMoreButton={shouldShowShowMoreButton}
            >
              {children}
            </CommunitySwitcherCommunityList>
          </>
        )}
        {showCommunities && showNewCommunityButton && (
          <>
            <CommunitySwitcherDivider />
            <CommunitySwitcherNewCommunityButton />
          </>
        )}
      </div>
    </div>
  );
};
